import { Suspense, useEffect } from "react";
import "./App.css";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import actions from "../constants/DefaultAllowedActions";
import ErrorBoundary from "./../shared/components/ErrorBoundary";
import GlobalLoader from "./../shared/components/Loader";
import { lazyWithRetry } from "./../shared/services/shared";
import { useSelector } from "react-redux";
import { useVersionNotifier } from "../utils/useVersionManager";
import { checkAppVersion } from "../utils/versionCheck";
import RouteWrapper from "../shared/components/RouteWrapper";
import ExpiredConversationLinkPage from "./Bot/conversation/ExpiredConversationLinkPage";

// ===== Lazy imports =====
const Login = lazyWithRetry(() => import("./user/login/Login"));
const ForgetPassword = lazyWithRetry(() => import("./user/forgetPassword/ForgetPassword"));
const ResetPassword = lazyWithRetry(() => import("./user/resetPassword/ResetPassword"));
const OTPVerification = lazyWithRetry(() => import("./user/verificationOTP/otpVerification"));
const Layout = lazyWithRetry(() => import("./Layout"));
const NotFound = lazyWithRetry(() => import("./layout/not-found/NotFound"));
const ChatClientHelp = lazyWithRetry(() => import("./ChatHelp/ChatClientHelp"));
const DownloadCsv = lazyWithRetry(() => import("./common/DownloadCsv"));

// ===== RenderApp handles routing logic =====
const RenderApp = (props) => {
  const location = useLocation();
  const { user = {} } = useSelector((state) => state?.user || {});
  const { isLoading } = useSelector((state) => state);
  useVersionNotifier();

  const isAllowed = () => {
    const path = location.pathname;
    const identity = user?.componentIndentity || [];
    if (actions.includes(path)) return true;
    const allowedPatterns = [
      "/analytics/page",
      "/admin/create/role",
      "/admin/create/page/",
      "/admin/tenantconfigurations",
      "/kbarticle",
      "/admin/tenantbots",
      "/admin/botconfig",
      "/admin/ticketforms/create",
      "/admin/salesforce/ticketforms",
      "/templates/update",
      "/admin/roles",
      "/admin/uploadusers",
      "/users",
      "/admin/role/users",
      "/nudging/campaign/list",
      "/insights",
      "/admin/client/credentials",
    ];

    if (allowedPatterns.some((pattern) => path.includes(pattern))) return true;
    return identity.includes(path);
  };

  const publicPaths = [
    "/login",
    "/ForgetPassword",
    "/reset-password",
    "/verifyotp",
    "/link-expired",
    "/download",
  ];

  useEffect(() => {
    const runCheck = () => checkAppVersion();
    runCheck();
    const interval = setInterval(runCheck, 300000);
    return () => clearInterval(interval);
  }, []);
  // allow dynamic help route
  const isPublic =
    publicPaths.some((p) => location.pathname.startsWith(p)) ||
    /^\/[^/]+\/help$/.test(location.pathname);

  if (!user?.isLoggedIn && !isPublic) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (!isPublic && !isAllowed()) {
    if (isLoading || props.isLoading) {
      return <GlobalLoader />;
    }
    return <RouteWrapper Component={NotFound} {...props} />;
  }

  return (
    <Suspense fallback={<GlobalLoader />}>
      <Routes>
        {/* Public route */}
        <Route
          path="/:botId/help"
          element={<RouteWrapper Component={ChatClientHelp} {...props} />}
        />

        <Route
          path="/download/conversations/:conversationId"
          element={<RouteWrapper Component={DownloadCsv} {...props} />}
        />

        <Route
          path="/link-expired"
          element={<RouteWrapper Component={ExpiredConversationLinkPage} {...props} />}
        />

        {/* Auth routes */}
        <Route path="/login" element={<RouteWrapper Component={Login} {...props} />} />
        <Route
          path="/verifyotp"
          element={<RouteWrapper Component={OTPVerification} {...props} />}
        />
        <Route
          path="/ForgetPassword"
          element={<RouteWrapper Component={ForgetPassword} {...props} />}
        />
        <Route
          path="/reset-password/:token"
          element={<RouteWrapper Component={ResetPassword} {...props} />}
        />

        <Route path="/*" element={<RouteWrapper Component={Layout} {...props} />} />
        <Route path="*" element={<RouteWrapper Component={NotFound} {...props} />} />
      </Routes>
    </Suspense>
  );
};

// ===== Root App =====
const App = (props) => {
  return (
    <Routes>
      <Route
        path="*"
        element={
          <ErrorBoundary {...props}>
            <RenderApp {...props} />
          </ErrorBoundary>
        }
      />
    </Routes>
  );
};

export default App;
